import React, { useEffect, useRef, useState, useCallback } from 'react';
import * as cornerstone from 'cornerstone-core';
import * as cornerstoneTools from 'cornerstone-tools';
import * as cornerstoneMath from 'cornerstone-math';
import Hammer from 'hammerjs';
import MeasurementControls from '../MeasurementControls';
import PanImageControls from '../PanImageControls';
import ExamList from '../ExamList';
import MobileNavigationButtons from '../MobileNavigationButtons';
import {
  ImageViewerContainer,
  DicomImageContainer,
  CornerstoneImage,
  ControlsContainer,
  SidebarContainer,
  MainContent,
  ImageInfoLabel,
} from './styles';

// Configuração das referências externas para Cornerstone Tools
cornerstoneTools.external.cornerstone = cornerstone;
cornerstoneTools.external.cornerstoneMath = cornerstoneMath;
cornerstoneTools.external.Hammer = Hammer;

// Inicialização do Cornerstone Tools
cornerstoneTools.init({
  mouseEnabled: true,
  touchEnabled: true,
  globalToolSyncEnabled: true,
});

// Adição de ferramentas básicas
cornerstoneTools.addTool(cornerstoneTools.LengthTool);
cornerstoneTools.addTool(cornerstoneTools.PanTool);
cornerstoneTools.addTool(cornerstoneTools.WwwcTool);
cornerstoneTools.addTool(cornerstoneTools.ZoomTool);
cornerstoneTools.addTool(cornerstoneTools.ZoomTouchPinchTool);

// Personalização da aparência das ferramentas
cornerstoneTools.toolColors.setActiveColor('rgb(255, 255, 0)');
cornerstoneTools.toolColors.setToolColor('rgb(0, 255, 0)');
cornerstoneTools.toolStyle.setToolWidth(2);

// Configuração das ferramentas padrão
cornerstoneTools.setToolActive('Wwwc', { mouseButtonMask: 1 });
cornerstoneTools.setToolActive('Zoom', { mouseButtonMask: 2, touchEnabled: false });
cornerstoneTools.setToolActive('ZoomTouchPinch', {});
cornerstoneTools.setToolPassive('Pan');
cornerstoneTools.setToolPassive('Length');

const ImageViewer = ({
  selectedRecord,
  loadDicomImage,
  zipFiles,
  loadedImages,
  setLoadedImages,
  records,
  organizedRecords,
  onSeriesSelect,
  loading,
  error,
  currentSeries,
  onImageSelect,
}) => {
  const dicomImageRef = useRef(null);
  const [currentStudyUid, setCurrentStudyUid] = useState(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  // Guarda WW/WC por estudo se quiser manter dentro do mesmo estudo
  const studyViewportRef = useRef({});

  // Para manter zoom/pan dentro do mesmo estudo (se quiser)
  const lastViewportRef = useRef(null);

  const [isMeasuring, setIsMeasuring] = useState(false);
  const [isPanning, setIsPanning] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isCtrlPressed, setIsCtrlPressed] = useState(false);
  const [isEnabled, setIsEnabled] = useState(false); // Estado para rastrear habilitação
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Função para lidar com mudanças no modo de medição
  const handleMeasuringChange = (active) => {
    if (active) setIsPanning(false);
    setIsMeasuring(active);
  };

  // Função para lidar com mudanças no modo de pan
  const handlePanChange = (active) => {
    if (active) setIsMeasuring(false);
    setIsPanning(active);
  };

  // Função para resetar o viewport para os valores padrão
  const resetViewportToDefault = useCallback(async () => {
    if (!dicomImageRef.current) return;
    const element = dicomImageRef.current;
    const image = cornerstone.getImage(element);
    if (!image) return;

    try {
      const defaultViewport = cornerstone.getDefaultViewportForImage(element, image);
      cornerstone.setViewport(element, defaultViewport);
      cornerstone.fitToWindow(element);
    } catch (error) {
      console.error('❌ Erro ao resetar viewport para valores padrão do estudo:', error);
    }
  }, []);

  // Função para exibir a imagem selecionada
  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  const displayImage = useCallback(
    async (studyInstanceUID) => {
      try {
        setIsTransitioning(true);
        const image = await loadDicomImage(
          selectedRecord.referencedFile,
          zipFiles,
          loadedImages,
          setLoadedImages
        );
        if (!image || !dicomImageRef.current) return;

        const element = dicomImageRef.current;
        await cornerstone.displayImage(element, image);

        const storedViewport = studyViewportRef.current[studyInstanceUID];
        const vp = cornerstone.getViewport(element);

        if (storedViewport) {
          Object.assign(vp.voi, {
            windowWidth: storedViewport.windowWidth,
            windowCenter: storedViewport.windowCenter,
          });
          vp.invert = storedViewport.invert;
        }

        if (lastViewportRef.current) {
          Object.assign(vp, {
            scale: lastViewportRef.current.scale,
            translation: { ...lastViewportRef.current.translation },
          });
        }

        cornerstone.setViewport(element, vp);

        if (studyInstanceUID && studyInstanceUID !== currentStudyUid) {
          setCurrentStudyUid(studyInstanceUID);
          resetViewportToDefault();
        }
      } catch (error) {
        console.error(`❌ Erro ao exibir imagem para studyInstanceUID: ${studyInstanceUID}`, error);
      } finally {
        setIsTransitioning(false);
      }
    },
    [
      selectedRecord,
      zipFiles,
      loadedImages,
      setLoadedImages,
      currentStudyUid,
      resetViewportToDefault,
    ]
  );

  // Função para lidar com o evento de imagem renderizada
  const onImageRendered = useCallback((element, currentStudyUid) => {
    if (currentStudyUid) {
      const viewport = cornerstone.getViewport(element);
      if (viewport) {
        studyViewportRef.current[currentStudyUid] = {
          windowWidth: viewport.voi.windowWidth,
          windowCenter: viewport.voi.windowCenter,
          invert: viewport.invert,
        };
      }
    }
  }, []);

  // Inicialização do Cornerstone na montagem do componente
  useEffect(() => {
    const element = dicomImageRef.current;
    if (element && !element.cornerstoneEnabled) {
      cornerstone.enable(element);
      element.cornerstoneEnabled = true;
      element.style.background = '#000';

      cornerstoneTools.addToolForElement(element, cornerstoneTools.WwwcTool);
      cornerstoneTools.addToolForElement(element, cornerstoneTools.ZoomTool);
      cornerstoneTools.addToolForElement(element, cornerstoneTools.PanTool);
      cornerstoneTools.addToolForElement(element, cornerstoneTools.LengthTool);
      cornerstoneTools.addToolForElement(element, cornerstoneTools.ZoomTouchPinchTool);

      cornerstoneTools.setToolActiveForElement(element, 'Wwwc', { mouseButtonMask: 1 });
      cornerstoneTools.setToolActiveForElement(element, 'Zoom', { mouseButtonMask: 2, touchEnabled: false });
      cornerstoneTools.setToolActiveForElement(element, 'ZoomTouchPinch', {});
      cornerstoneTools.setToolPassiveForElement(element, 'Pan');
      cornerstoneTools.setToolPassiveForElement(element, 'Length');

      const hammer = new Hammer(element);
      hammer.get('pinch').set({ enable: true });

      setIsEnabled(true); // Atualiza o estado indicando que o elemento está habilitado
    }

    // Cleanup ao desmontar o componente
    return () => {
      const element = dicomImageRef.current;
      if (element?.cornerstoneEnabled) {
        cornerstone.disable(element);
        setIsEnabled(false); // Atualiza o estado indicando que o elemento foi desabilitado
      }
    };
  }, []); // Executa apenas na montagem e desmontagem

  // Ativação/Passivação das ferramentas com base no estado
  useEffect(() => {
    if (!isEnabled) return; // Garante que o elemento está habilitado

    const element = dicomImageRef.current;
    if (!element) return;

    if (isPanning) {
      cornerstoneTools.setToolPassiveForElement(element, 'Wwwc');
      cornerstoneTools.setToolPassiveForElement(element, 'Length');
      cornerstoneTools.setToolActiveForElement(element, 'Pan', { mouseButtonMask: 1 });
    } else {
      cornerstoneTools.setToolPassiveForElement(element, 'Pan');
      cornerstoneTools.setToolActiveForElement(element, 'Wwwc', { mouseButtonMask: 1 });
    }

    if (isMeasuring) {
      cornerstoneTools.setToolActiveForElement(element, 'Length', { mouseButtonMask: 1 });
    } else {
      cornerstoneTools.setToolPassiveForElement(element, 'Length');
    }

    cornerstone.updateImage(element);
  }, [isPanning, isMeasuring, isEnabled]);

  // Listener para o evento 'cornerstoneimagerendered'
  useEffect(() => {
    if (!isEnabled) return; // Garante que o elemento está habilitado

    const element = dicomImageRef.current;
    if (!element) return;

    const handleImageRendered = () => onImageRendered(element, currentStudyUid);

    element.addEventListener('cornerstoneimagerendered', handleImageRendered);
    return () => {
      element.removeEventListener('cornerstoneimagerendered', handleImageRendered);
    };
  }, [currentStudyUid, onImageRendered, isEnabled]);

  // Pré-carregamento de imagens adjacentes
  useEffect(() => {
    if (!currentSeries?.images || isTransitioning) return;

    const preloadImages = async () => {
      const currentIndex = currentSeries.images.findIndex((img) => img === selectedRecord);
      if (currentIndex === -1) return;

      const indicesToLoad = [
        (currentIndex + 1) % currentSeries.images.length,
        (currentIndex - 1 + currentSeries.images.length) % currentSeries.images.length,
      ];

      for (const idx of indicesToLoad) {
        const imageRecord = currentSeries.images[idx];
        if (imageRecord && !loadedImages[imageRecord.referencedFile]) {
          try {
            await loadDicomImage(
              imageRecord.referencedFile,
              zipFiles,
              loadedImages,
              setLoadedImages
            );
          } catch (error) {
            console.error('❌ Erro ao pré-carregar imagem:', error);
          }
        }
      }
    };

    preloadImages();
  }, [
    selectedRecord,
    currentSeries,
    isTransitioning,
    loadDicomImage,
    zipFiles,
    loadedImages,
    setLoadedImages,
  ]);

  // Exibição da imagem selecionada
  useEffect(() => {
    if (!selectedRecord || !isEnabled) return; // Garante que o elemento está habilitado
    const studyInstanceUID = currentSeries.seriesId || '';
    displayImage(studyInstanceUID);
  }, [selectedRecord, currentSeries, displayImage, isEnabled]);

  // Listeners de teclado e roda do mouse agrupados
  useEffect(() => {
    if (!isEnabled) return; // Garante que o elemento está habilitado

    const handleKeyDown = (e) => {
      if (e.key === 'Control') {
        setIsCtrlPressed(true);
      }

      if (e.key === 'Delete' && dicomImageRef.current) {
        const lengthState = cornerstoneTools.getToolState(dicomImageRef.current, 'Length');
        if (lengthState?.data.length) {
          const lastMeasurement = lengthState.data[lengthState.data.length - 1];
          cornerstoneTools.removeToolState(dicomImageRef.current, 'Length', lastMeasurement);
          cornerstone.updateImage(dicomImageRef.current);
        }
      }

      if ((e.key === 'ArrowRight' || e.key === 'ArrowLeft') && currentSeries?.images) {
        e.preventDefault();
        if (isTransitioning) return;

        const currentIndex = currentSeries.images.findIndex((img) => img === selectedRecord);
        if (currentIndex === -1) return;

        let nextIndex;
        if (e.key === 'ArrowRight') {
          nextIndex = (currentIndex + 1) % currentSeries.images.length;
        } else {
          nextIndex =
            (currentIndex - 1 + currentSeries.images.length) % currentSeries.images.length;
        }

        const nextImage = currentSeries.images[nextIndex];
        if (dicomImageRef.current) {
          lastViewportRef.current = cornerstone.getViewport(dicomImageRef.current);
        }

        if (!loadedImages[nextImage.referencedFile]) {
          loadDicomImage(nextImage.referencedFile, zipFiles, loadedImages, setLoadedImages)
            .then(() => onImageSelect(nextImage))
            .catch((err) => console.error('❌ Erro ao carregar próxima imagem:', err));
        } else {
          onImageSelect(nextImage);
        }
      }
    };

    const handleKeyUp = (e) => {
      if (e.key === 'Control') {
        setIsCtrlPressed(false);
      }
    };

    const handleWheel = async (event) => {
      event.preventDefault();

      if (!dicomImageRef.current || !dicomImageRef.current.cornerstoneEnabled) return;

      if (isCtrlPressed) {
        const viewport = cornerstone.getViewport(dicomImageRef.current);
        if (!viewport) return;

        const delta = -event.deltaY * 0.001;
        let newScale = viewport.scale * (1 + delta);
        newScale = Math.max(newScale, 0.05);
        newScale = Math.min(newScale, 20);

        viewport.scale = newScale;
        viewport.translation.x = 0;
        viewport.translation.y = 0;

        cornerstone.setViewport(dicomImageRef.current, viewport);
      } else {
        if (!currentSeries?.images || isTransitioning) return;

        const currentIndex = currentSeries.images.findIndex((img) => img === selectedRecord);
        if (currentIndex === -1) return;

        let nextIndex;
        if (event.deltaY > 0) {
          nextIndex = (currentIndex + 1) % currentSeries.images.length;
        } else {
          nextIndex =
            (currentIndex - 1 + currentSeries.images.length) % currentSeries.images.length;
        }

        const nextImage = currentSeries.images[nextIndex];
        if (dicomImageRef.current) {
          lastViewportRef.current = cornerstone.getViewport(dicomImageRef.current);
        }

        if (!loadedImages[nextImage.referencedFile]) {
          try {
            await loadDicomImage(nextImage.referencedFile, zipFiles, loadedImages, setLoadedImages);
          } catch (err) {
            console.error('❌ Erro ao carregar próxima imagem:', err);
          }
        }
        onImageSelect(nextImage);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    const dicomElement = dicomImageRef.current;
    if (dicomElement) {
      dicomElement.addEventListener('wheel', handleWheel, { passive: false });
    }

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
      if (dicomElement) {
        dicomElement.removeEventListener('wheel', handleWheel, { passive: false });
      }
    };
  }, [
    isTransitioning,
    currentSeries,
    selectedRecord,
    isCtrlPressed,
    loadDicomImage,
    zipFiles,
    loadedImages,
    setLoadedImages,
    onImageSelect,
    isEnabled,
  ]);

  // Função para navegar entre imagens (mobile)
  const handleImageSelect = useCallback(async (direction) => {
    if (!currentSeries?.images || !selectedRecord) return;
    
    const currentIndex = currentSeries.images.findIndex((img) => img === selectedRecord);
    if (currentIndex === -1) return;
    
    const nextIndex = currentIndex + direction;
    if (nextIndex >= 0 && nextIndex < currentSeries.images.length) {
      const nextRecord = currentSeries.images[nextIndex];
      if (nextRecord) {
        // Salvar o viewport atual antes de mudar a imagem
        if (dicomImageRef.current) {
          const element = dicomImageRef.current;
          const viewport = cornerstone.getViewport(element);
          lastViewportRef.current = {
            scale: viewport.scale,
            translation: { ...viewport.translation },
          };
        }
        
        await displayImage(nextRecord.studyInstanceUID);
        onImageSelect(nextRecord);
      }
    }
  }, [currentSeries, selectedRecord, displayImage, onImageSelect]);

  // Função para obter informações da imagem atual
  const getImageInfo = () => {
    if (!currentSeries || !selectedRecord) return null;
    const currentIndex = currentSeries.images.findIndex((img) => img === selectedRecord) + 1;
    return (
      <>
        <div className="info-row">
          <span className="label">Im:</span>
          <span className="value">
            {currentIndex}/{currentSeries.images.length}
          </span>
        </div>
        <div className="info-row">
          <span className="label">Ser:</span>
          <span className="value">{currentSeries.seriesNumber}</span>
        </div>
      </>
    );
  };

  return (
    <ImageViewerContainer>
      <ExamList
        loading={loading}
        error={error}
        records={records}
        selectedRecord={selectedRecord}
        onSeriesSelect={onSeriesSelect}
        loadDicomImage={loadDicomImage}
        organizedRecords={organizedRecords}
        zipFiles={zipFiles}
        loadedImages={loadedImages}
        setLoadedImages={setLoadedImages}
        isMenuOpen={isMenuOpen}
        setIsMenuOpen={setIsMenuOpen}
      />
      <MainContent isMenuOpen={isMenuOpen}>
        <DicomImageContainer style={{ touchAction: 'none' }}>
          <CornerstoneImage
            id="dicomImage"
            ref={dicomImageRef}
            onContextMenu={(e) => e.preventDefault()}
            style={{
              width: '100%',
              height: '100%',
              touchAction: 'none',
              cursor: isMeasuring ? 'crosshair' : isPanning ? 'move' : 'default',
            }}
          />
          {currentSeries && selectedRecord && <ImageInfoLabel>{getImageInfo()}</ImageInfoLabel>}

          <ControlsContainer>
            {!isMobile && (
              <MeasurementControls
                imageElement={dicomImageRef.current}
                onMeasuringChange={handleMeasuringChange}
                isMeasuring={isMeasuring}
              />
            )}
            <PanImageControls
              imageElement={dicomImageRef.current}
              onPanChange={handlePanChange}
              isPanning={isPanning}
            />
          </ControlsContainer>
        </DicomImageContainer>
      </MainContent>
      {isMobile && <MobileNavigationButtons onImageSelect={handleImageSelect} />}
    </ImageViewerContainer>
  );
};

export default ImageViewer;
